import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { AuthService } from '../../../../../shared/services/auth.service';
import { CartService } from '../../../../../shared/services/cart.service';
import {
  ShippingConfigService,
  ShippingCountry,
} from '../../../../../core/services/shipping-config.service';
import {
  ShippingStateService,
  ShippingState,
  FedexShippingOption,
} from '../../../../../shared/services/shipping-state.service';
import { CurrencyService } from '../../../../../core/services/currency.service';
import {
  getUserCheckoutAddresses,
  getUserCheckoutBuyerType,
} from '../../../../../shared/checkout-address.util';

@Component({
  selector: 'app-billing-step',
  standalone: false,
  templateUrl: './billing-step.component.html',
  styleUrl: './billing-step.component.scss',
})
export class BillingStepComponent implements OnChanges, OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;

  currentUser: any = null;
  isEditMode: boolean = false;
  isCheckingPostalCode: boolean = false;
  postalCodeInvalid: boolean = false;

  countries$: Observable<ShippingCountry[]>;
  shippingState$: Observable<ShippingState>;
  countries: ShippingCountry[] = [];

  // Custom searchable country dropdown
  isCountryDropdownOpen: boolean = false;
  countrySearchText: string = '';
  @ViewChild('countrySearchInput') countrySearchInputElement?: ElementRef<HTMLInputElement>;

  private userSubscription?: Subscription;
  private formSubscription?: Subscription;
  private accountTypeSubscription?: Subscription;
  private countriesSubscription?: Subscription;
  private postalLookupSubscription?: Subscription;
  private lastAutoFilledCity = '';
  private lastAutoFilledState = '';
  private lastAddressKey = '';
  postalValidationMessage: string | null = null;

  constructor(
    private http: HttpClient,
    public authService: AuthService,
    public shippingConfigService: ShippingConfigService,
    public shippingStateService: ShippingStateService,
    public currencyService: CurrencyService,
    private cartService: CartService,
    private readonly elementRef: ElementRef,
  ) {
    this.countries$ = this.shippingConfigService.getCountries();
    this.shippingState$ = this.shippingStateService.state$;
  }

  ngOnInit(): void {
    this.countriesSubscription = this.countries$.subscribe((countries) => {
      this.countries = countries;
      this.normalizeCountryCodeInForm();
    });

    this.currentUser = this.authService.isLoggedIn()
      ? this.authService.getCurrentUser() || this.getStoredUser()
      : null;
    this.populateFormFromUser();

    // Fetch full fresh profile from backend if logged in
    if (this.authService.isLoggedIn()) {
      this.authService.getProfile().subscribe({
        next: (response: any) => {
          const profileUser = response?.data?.[0]?.user || response?.user;
          if (profileUser) {
            this.currentUser = { ...(this.currentUser || {}), ...profileUser };
            try {
              localStorage.setItem('clientUser', JSON.stringify(this.currentUser));
            } catch (e) { }
            this.populateFormFromUser();
          }
        },
        error: (err) => {
          console.warn('Could not fetch user profile in billing step', err);
        }
      });
    }

    this.userSubscription = this.authService.currentUser$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.populateFormFromUser();
      }
    });

    this.autoDetectUserCountry();
    this.setupAddressValidation();
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.accountTypeSubscription?.unsubscribe();
    this.countriesSubscription?.unsubscribe();
    this.postalLookupSubscription?.unsubscribe();
  }

  private getStoredUser(): any {
    try {
      const str = localStorage.getItem('clientUser') || sessionStorage.getItem('clientUser');
      if (str) return JSON.parse(str);
    } catch (e) { }
    return null;
  }

  get isCompanyUser(): boolean {
    if (!this.authService.isLoggedIn()) {
      return this.formGroup?.get('accountType')?.value === 'company';
    }

    const u = this.currentUser;
    if (!u) return false;
    return getUserCheckoutBuyerType(u) === 'company';
  }

  get isIndividualUser(): boolean {
    return !this.isCompanyUser;
  }

  updateCompanyValidators(): void {
    if (!this.formGroup) return;
    const control = this.formGroup.get('companyName');
    if (control) {
      if (this.isCompanyUser) {
        control.setValidators([
          Validators.required,
          Validators.pattern(/\S/),
          Validators.maxLength(200),
        ]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  get isCompanyDataComplete(): boolean {
    if (!this.formGroup) return false;
    const v = this.formGroup.value;
    const baseComplete = !!(
      v.postcodeTown?.trim() &&
      v.postalCode?.trim() &&
      v.city?.trim() &&
      v.otherCountry?.trim() &&
      v.firstName?.trim() &&
      v.lastName?.trim() &&
      v.phoneNumber?.trim() &&
      v.email?.trim()
    );
    if (this.isCompanyUser) {
      return baseComplete && !!(v.companyName?.trim());
    }
    return baseComplete;
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
  }

  get isDeliveryAddressComplete(): boolean {
    if (!this.formGroup) return false;
    const value = this.getSelectedAddress(this.formGroup.getRawValue());
    return !!(
      value.otherCountry?.trim() &&
      value.postcodeTown?.trim() &&
      value.postalCode?.trim() &&
      value.city?.trim()
    );
  }

  retryShippingRateLookup(): void {
    this.triggerAddressValidation();
  }

  private normalizeCountryCodeInForm(): void {
    if (!this.formGroup || this.countries.length === 0) return;
    const address = this.getSelectedAddress(this.formGroup.getRawValue());
    const currentVal = address.otherCountry;
    if (currentVal) {
      const match = this.countries.find(
        (c) =>
          c.code.toUpperCase() === currentVal.toUpperCase() ||
          c.name.toLowerCase() === currentVal.toLowerCase()
      );
      if (match && match.code !== currentVal) {
        this.formGroup.get(address.countryControl)?.setValue(match.code, { emitEvent: false });
      }
    }
    this.triggerAddressValidation();
  }

  private setupAddressValidation(): void {
    if (!this.formGroup) return;
    this.updateDeliveryAddressValidators();

    if (!this.authService.isLoggedIn()) {
      this.accountTypeSubscription = this.formGroup
        .get('accountType')
        ?.valueChanges.subscribe((accountType) => {
          if (accountType !== 'company') {
            this.formGroup.patchValue(
              {
                companyName: '',
                industrysector: '',
                vatNumber: '',
                orderingParty: '',
                departmentOrder: '',
              },
              { emitEvent: false },
            );
          }
        });
    }

    this.formSubscription = this.formGroup.valueChanges
      .pipe(
        tap((value) => {
          this.updateDeliveryAddressValidators();
          const addressKey = this.getAddressKey(this.getSelectedAddress(value));
          if (this.lastAddressKey && addressKey !== this.lastAddressKey) {
            this.shippingStateService.reset();
            this.postalLookupSubscription?.unsubscribe();
            this.isCheckingPostalCode = false;
          }
        }),
        debounceTime(350),
        distinctUntilChanged(
          (prev, curr) =>
            this.getAddressKey(this.getSelectedAddress(prev)) ===
            this.getAddressKey(this.getSelectedAddress(curr)),
        ),
      )
      .subscribe(() => {
        this.triggerAddressValidation();
      });

    // Check if initial values are already present
    this.triggerAddressValidation();
  }

  private updateDeliveryAddressValidators(): void {
    if (!this.formGroup) return;
    this.updateCompanyValidators();
    const sameAsBilling =
      this.formGroup.get('deliverySameAsBilling')?.value !== false;
    for (const controlName of [
      'deliveryAddress',
      'deliveryPostalCode',
      'deliveryCity',
      'deliveryCountry',
    ]) {
      const control = this.formGroup.get(controlName);
      if (!control) continue;
      if (sameAsBilling) {
        control.clearValidators();
      } else {
        control.setValidators([Validators.required]);
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  triggerAddressValidation(): void {
    if (!this.formGroup) return;
    this.postalLookupSubscription?.unsubscribe();
    this.isCheckingPostalCode = false;

    const address = this.getSelectedAddress(this.formGroup.getRawValue());
    const rawCountry = (address.otherCountry || '').trim();
    const postalCode = (address.postalCode || '').trim();
    const city = (address.city || '').trim();
    const street = (address.postcodeTown || '').trim();
    this.lastAddressKey = this.getAddressKey(address);

    // Normalize country to 2-letter ISO code if needed
    const countryCode = this.shippingConfigService.resolveCountryCode(rawCountry) || rawCountry;
    if (countryCode && countryCode !== rawCountry && countryCode.length === 2) {
      this.formGroup.get(address.countryControl)?.setValue(countryCode, { emitEvent: false });
    }

    if (countryCode) {
      this.currencyService.onCountryChange(countryCode);
    }

    this.shippingStateService.reset();
    this.postalValidationMessage = null;

    if (!countryCode || !postalCode) {
      this.postalCodeInvalid = false;
      this.clearPostalCodeError(address.postalControl);
      return;
    }

    this.isCheckingPostalCode = true;
    this.postalLookupSubscription = this.shippingConfigService
      .lookupPostalCode(countryCode, postalCode)
      .subscribe((res) => {
        this.isCheckingPostalCode = false;
        if (res.valid) {
          this.postalCodeInvalid = false;
          this.clearPostalCodeError(address.postalControl);
          const cityControl = this.formGroup.get(address.cityControl);
          const stateControl = this.formGroup.get(address.stateControl);

          // Auto-fill City if empty or previously auto-filled
          if (res.city) {
            const currentCity = cityControl?.value?.trim();
            if (!currentCity || this.lastAutoFilledCity === currentCity) {
              cityControl?.setValue(res.city, { emitEvent: false });
              this.lastAutoFilledCity = res.city;
            }
          }

          // Auto-fill State/Province if empty or previously auto-filled
          if (res.state) {
            const currentState = stateControl?.value?.trim();
            if (!currentState || this.lastAutoFilledState === currentState) {
              stateControl?.setValue(res.state, { emitEvent: false });
              this.lastAutoFilledState = res.state;
            }
          }

          const resolvedCity = (cityControl?.value || '').trim();
          // If city (>= 2 chars) and street are valid, call rate calculation
          if (resolvedCity.length >= 2 && street.length > 0) {
            const cartItems = this.cartService.getCart();
            this.shippingStateService.fetchRates(
              cartItems,
              countryCode,
              postalCode,
              resolvedCity,
              street,
              (stateControl?.value || '').trim(),
            );
          }
        } else {
          this.postalCodeInvalid = true;
          this.postalValidationMessage = null;
          this.setPostalCodeError(address.postalControl);
          this.shippingStateService.reset();
        }
      }, () => {
        this.isCheckingPostalCode = false;
        this.postalCodeInvalid = false;
        this.postalValidationMessage =
          'We could not verify this postal code right now. Please check it and try again.';
        this.shippingStateService.reset();
      });
  }

  private getAddressKey(value: any): string {
    return [
      value?.otherCountry || '',
      value?.postalCode || '',
      value?.city || '',
      value?.postcodeTown || '',
    ].map((part: string) => String(part).trim().toLowerCase()).join('|');
  }

  private getSelectedAddress(value: any) {
    if (value?.deliverySameAsBilling !== false) {
      return {
        otherCountry: value?.otherCountry || '',
        postalCode: value?.postalCode || '',
        city: value?.city || '',
        postcodeTown: value?.postcodeTown || '',
        countryControl: 'otherCountry',
        postalControl: 'postalCode',
        cityControl: 'city',
        stateControl: 'stateProvince',
      };
    }
    return {
      otherCountry: value?.deliveryCountry || '',
      postalCode: value?.deliveryPostalCode || '',
      city: value?.deliveryCity || '',
      postcodeTown: value?.deliveryAddress || '',
      countryControl: 'deliveryCountry',
      postalControl: 'deliveryPostalCode',
      cityControl: 'deliveryCity',
      stateControl: 'deliveryStateProvince',
    };
  }

  private clearPostalCodeError(controlName: string): void {
    const ctrl = this.formGroup.get(controlName);
    if (!ctrl) return;
    if (ctrl.errors && ctrl.errors['invalidPostalCode']) {
      const errs = { ...ctrl.errors };
      delete errs['invalidPostalCode'];
      ctrl.setErrors(Object.keys(errs).length > 0 ? errs : null);
    }
  }

  private setPostalCodeError(controlName: string): void {
    const ctrl = this.formGroup.get(controlName);
    if (!ctrl) return;
    const errs = { ...(ctrl.errors || {}), invalidPostalCode: true };
    ctrl.setErrors(errs);
    ctrl.markAsTouched();
  }

  selectShippingOption(option: FedexShippingOption): void {
    this.shippingStateService.selectOption(option);
  }

  formatShippingPrice(option: FedexShippingOption): string {
    if (!option || option.totalPrice === 0) {
      return 'Free';
    }
    const converted = this.currencyService.convertShipping(
      option.totalPrice,
      option.currency,
    );
    return this.currencyService.format(converted);
  }

  getDeliveryBadge(option: FedexShippingOption): { text: string; isFast: boolean } {
    if (!option) return { text: '', isFast: false };

    const isFast =
      option.serviceType.includes('EXPRESS') ||
      option.serviceType.includes('PRIORITY') ||
      option.serviceType.includes('FIRST');

    // 1. If explicit deliveryDate or timestamp exists
    if (option.deliveryDate) {
      try {
        const d = new Date(option.deliveryDate);
        if (!isNaN(d.getTime())) {
          const formatted = new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }).format(d);
          return {
            text: `Est. delivery by ${formatted}`,
            isFast,
          };
        }
      } catch (_) {}
    }

    // 2. If deliveryDays string exists (e.g. "2-4 working days" or "1-2 working days")
    if (option.deliveryDays) {
      const match = option.deliveryDays.match(/\d+/);
      if (match) {
        const days = parseInt(match[0], 10);
        const estDate = new Date();
        let added = 0;
        while (added < days) {
          estDate.setDate(estDate.getDate() + 1);
          const dow = estDate.getDay();
          if (dow !== 0 && dow !== 6) {
            added++;
          }
        }
        const formatted = new Intl.DateTimeFormat('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }).format(estDate);
        return {
          text: `Est. by ${formatted} (${option.deliveryDays})`,
          isFast: days <= 2,
        };
      }
      return { text: option.deliveryDays, isFast };
    }

    return {
      text: isFast ? 'Est. 1-2 business days' : 'Est. 2-4 business days',
      isFast,
    };
  }

  private populateFormFromUser(): void {
    if (!this.formGroup || !this.currentUser) return;
    this.updateCompanyValidators();

    const u = this.currentUser;

    const addresses = getUserCheckoutAddresses(u);
    const companyName  = u.company        || u.organizationName || '';
    const vatNumber    = u.vatNumber      || u.vat_number      || '';
    const department   = u.department     || u.industrysector  || '';
    const street       = addresses.billing.street;
    const state        = u.stateProvince  || u.state           || '';
    const postalCode   = addresses.billing.postalCode;
    const city         = addresses.billing.city;
    const country      = addresses.billing.country;
    const firstName    = u.firstName      || '';
    const lastName     = u.lastName       || '';
    const phone        = u.phone          || u.mobileNumber    || '';
    const email        = u.email          || '';

    const patch: any = {};
    patch.accountType = this.isCompanyUser ? 'company' : 'individual';
    if (this.isCompanyUser) {
      if (companyName)  patch.companyName   = companyName;
      if (vatNumber)    patch.vatNumber     = vatNumber;
      if (department)   patch.industrysector= department;
    } else {
      patch.companyName    = '';
      patch.vatNumber      = '';
      patch.industrysector = '';
      patch.orderingParty  = '';
      patch.departmentOrder= '';
    }

    if (street)       patch.postcodeTown  = street;
    if (state)        patch.stateProvince = state;
    if (postalCode)   patch.postalCode    = postalCode;
    if (city)         patch.city          = city;
    // User's account country ALWAYS takes precedence over sidebar selection
    if (country) {
      const iso = this.shippingConfigService.resolveCountryCode(country);
      patch.otherCountry = iso || country;
    }
    if (firstName)    patch.firstName     = firstName;
    if (lastName)     patch.lastName      = lastName;
    if (phone)        patch.phoneNumber   = phone;
    if (email)        patch.email         = email;
    patch.deliverySameAsBilling = addresses.deliverySameAsBilling;
    if (!addresses.deliverySameAsBilling) {
      patch.deliveryRecipientName = addresses.deliveryRecipient;
      patch.deliveryCompanyName = addresses.deliveryCompany;
      patch.deliveryAddress = addresses.delivery.street;
      patch.deliveryPostalCode = addresses.delivery.postalCode;
      patch.deliveryCity = addresses.delivery.city;
      patch.deliveryStateProvince = u.deliveryStateProvince || '';
      patch.deliveryCountry = addresses.delivery.country;
      patch.deliveryPhone = addresses.deliveryPhone;
    }

    const safePatch = Object.fromEntries(
      Object.entries(patch).filter(
        ([key]) => key === 'accountType' || !this.formGroup.get(key)?.dirty,
      ),
    );
    if (Object.keys(safePatch).length > 0) {
      this.formGroup.patchValue(safePatch);
    }
  }

  get displayCompanyName(): string {
    return this.formGroup?.get('companyName')?.value || this.currentUser?.company || this.currentUser?.deliveryOrgName || '';
  }

  get displayVatNumber(): string {
    return this.formGroup?.get('vatNumber')?.value || this.currentUser?.vatNumber || '';
  }

  get displayIndustry(): string {
    return this.formGroup?.get('industrysector')?.value || this.currentUser?.department || this.currentUser?.industrysector || '';
  }

  get displayStreet(): string {
    return this.formGroup?.get('postcodeTown')?.value || this.currentUser?.deliveryStreet || this.currentUser?.street || '';
  }

  get displayPostalCode(): string {
    return this.formGroup?.get('postalCode')?.value || this.currentUser?.deliveryZipCode || this.currentUser?.zipCode || '';
  }

  get displayCity(): string {
    return this.formGroup?.get('city')?.value || this.currentUser?.deliveryCity || this.currentUser?.city || '';
  }

  get displayCountry(): string {
    const val = this.formGroup?.get('otherCountry')?.value || this.currentUser?.deliveryCountry || this.currentUser?.country || '';
    const match = this.countries.find(
      (c) =>
        c.code.toUpperCase() === val.toUpperCase() ||
        c.name.toLowerCase() === val.toLowerCase()
    );
    return match ? match.name : val;
  }

  // --- Custom Searchable Country Dropdown ---
  toggleCountryDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
    if (this.isCountryDropdownOpen) {
      this.countrySearchText = '';
      setTimeout(() => {
        this.countrySearchInputElement?.nativeElement?.focus();
      }, 50);
    } else {
      this.formGroup?.get('otherCountry')?.markAsTouched();
    }
  }

  closeCountryDropdown(): void {
    if (this.isCountryDropdownOpen) {
      this.isCountryDropdownOpen = false;
      this.countrySearchText = '';
      this.formGroup?.get('otherCountry')?.markAsTouched();
    }
  }

  onCountrySearch(text: string): void {
    this.countrySearchText = text;
  }

  clearCountrySearch(event: MouseEvent): void {
    event.stopPropagation();
    this.countrySearchText = '';
    this.countrySearchInputElement?.nativeElement?.focus();
  }

  selectCountry(country: ShippingCountry, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.formGroup) return;
    this.formGroup.get('otherCountry')?.setValue(country.code);
    this.formGroup.get('otherCountry')?.markAsDirty();
    this.formGroup.get('otherCountry')?.markAsTouched();
    this.currencyService.onCountryChange(country.code);
    this.closeCountryDropdown();
  }

  get filteredCountries(): ShippingCountry[] {
    const list = this.countries || [];
    const query = (this.countrySearchText || '').trim().toLowerCase();
    if (!query) return list;
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isCountryDropdownOpen) return;
    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.querySelector('.country-dropdown-container')?.contains(target)) {
      this.closeCountryDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.closeCountryDropdown();
  }

  get displayState(): string {
    return this.formGroup?.get('stateProvince')?.value || this.currentUser?.stateProvince || this.currentUser?.state || '';
  }

  get displayOtherAddressInfo(): string {
    return this.formGroup?.get('otherAddressInfo')?.value || '';
  }

  get displayContactName(): string {
    const fn = this.formGroup?.get('firstName')?.value || this.currentUser?.firstName || '';
    const ln = this.formGroup?.get('lastName')?.value || this.currentUser?.lastName || '';
    return `${fn} ${ln}`.trim();
  }

  get displayPhone(): string {
    return this.formGroup?.get('phoneNumber')?.value || this.currentUser?.phone || this.currentUser?.mobileNumber || '';
  }

  get currentDialCode(): string {
    const country = this.formGroup?.get('otherCountry')?.value;
    return this.shippingConfigService.getDialCode(country) || '+';
  }

  get phonePlaceholder(): string {
    const code = this.currentDialCode;
    switch (code) {
      case '+216': return '55 123 456';
      case '+33':  return '6 12 34 56 78';
      case '+49':  return '170 1234567';
      case '+1':   return '202 555 0123';
      case '+44':  return '7911 123456';
      case '+48':  return '501 234 567';
      case '+39':  return '312 345 6789';
      case '+34':  return '612 34 56 78';
      default:     return '123 456 789';
    }
  }

  onPhoneBlur(): void {
    const ctrl = this.formGroup?.get('phoneNumber');
    if (!ctrl) return;
    const raw = (ctrl.value || '').trim();
    if (!raw) return;

    const dialCode = this.currentDialCode;
    if (raw.startsWith('+')) {
      return;
    }

    if (raw.startsWith('00')) {
      ctrl.setValue('+' + raw.slice(2).trim());
      ctrl.updateValueAndValidity();
      return;
    }

    let localNumber = raw;
    if (localNumber.startsWith('0') && localNumber.length > 1 && dialCode !== '+') {
      localNumber = localNumber.slice(1).trim();
    }

    if (dialCode && dialCode !== '+') {
      ctrl.setValue(`${dialCode} ${localNumber}`);
      ctrl.updateValueAndValidity();
    }
  }

  get displayEmail(): string {
    return this.formGroup?.get('email')?.value || this.currentUser?.email || '';
  }

  get displayOrderingParty(): string {
    return this.formGroup?.get('orderingParty')?.value || '';
  }

  get displayDepartmentOrder(): string {
    return this.formGroup?.get('departmentOrder')?.value || '';
  }

  get displayNoteBrocom(): string {
    return this.formGroup?.get('noteBrocom')?.value || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup'] && changes['formGroup'].currentValue) {
      // Re-populate from user whenever the formGroup input changes
      this.populateFormFromUser();
      this.autoDetectUserCountry();
    }
  }

  private autoDetectUserCountry(): void {
    if (!this.formGroup || !this.formGroup.get('otherCountry')) return;

    // If currentUser has an account country, prioritize it and do not overwrite with sidebar/IP
    const accountCountry = this.currentUser?.deliveryCountry || this.currentUser?.country || this.currentUser?.addresses?.[0]?.country;
    if (accountCountry) {
      this.setCountryFields(accountCountry);
      return;
    }

    const currentOtherCountry = this.formGroup.get('otherCountry')!.value;
    if (currentOtherCountry) return;

    // 1. First priority for guests/unauthenticated: Check user's selected country from Navbar / Country Selector sidebar
    const savedCountry = localStorage.getItem('brocom_selected_country');
    if (savedCountry) {
      try {
        const parsed = JSON.parse(savedCountry);
        if (parsed?.name) {
          this.setCountryFields(parsed.name);
          return;
        }
      } catch (e) { }
    }

    // 2. Fallback: IP-based detection if not yet selected in sidebar
    this.detectCountryFromIP();
  }

  private detectCountryFromIP(): void {
    // Try ipapi.co first (most reliable)
    this.http.get<any>('https://ipapi.co/json/').subscribe({
      next: (response) => {
        if (response && response.country_name) {
          this.setCountryFields(response.country_name);
        } else {
          this.tryAlternativeGeoService();
        }
      },
      error: (error) => {
        this.tryAlternativeGeoService();
      }
    });
  }

  private tryAlternativeGeoService(): void {
    // Try ip-api.com as backup
    this.http.get<any>('http://ip-api.com/json/').subscribe({
      next: (response) => {
        if (response && response.country) {
          this.setCountryFields(response.country);
        } else {
          this.tryBrowserGeolocation();
        }
      },
      error: (error) => {
        this.tryBrowserGeolocation();
      }
    });
  }

  private tryBrowserGeolocation(): void {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Use reverse geocoding to get country from coordinates
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          // Use a reverse geocoding service
          this.http.get<any>(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`).subscribe({
            next: (response) => {
              if (response && response.countryName) {
                this.setCountryFields(response.countryName);
              }
              // Don't set any default - let user fill it manually
            },
            error: (error) => {
              // Don't set any default - let user fill it manually
            }
          });
        },
        (error) => {
          // Don't set any default - let user fill it manually
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    } else {
      // Don't set any default - let user fill it manually
    }
  }

  private setCountryFields(country: string): void {
    if (this.formGroup && country) {
      // Resolve full name or code to 2-letter ISO code
      const isoCode = this.shippingConfigService.resolveCountryCode(country);
      if (isoCode) {
        this.formGroup.get('otherCountry')!.setValue(isoCode);
        return;
      }
      // Fallback: try to find in cached countries list
      const match = this.countries.find(
        (c) =>
          c.code.toUpperCase() === country.toUpperCase() ||
          c.name.toLowerCase() === country.toLowerCase()
      );
      this.formGroup.get('otherCountry')!.setValue(match ? match.code : country);
    }
  }

  // Helper methods to check if sections have validation errors
  hasCompanyInformationErrors(): boolean {
    if (!this.isCompanyUser || !this.formGroup) return false;
    const companyFields = ['companyName'];
    return companyFields.some(field => {
      const control = this.formGroup.get(field);
      return control && control.invalid && control.touched;
    });
  }

  hasAddressInformationErrors(): boolean {
    if (!this.formGroup) return false;
    const addressFields = ['postcodeTown', 'postalCode', 'city', 'otherCountry'];
    return addressFields.some(field => {
      const control = this.formGroup.get(field);
      return control && control.invalid && control.touched;
    });
  }

  hasContactInformationErrors(): boolean {
    if (!this.formGroup) return false;
    const contactFields = ['firstName', 'lastName', 'phoneNumber', 'email'];
    return contactFields.some(field => {
      const control = this.formGroup.get(field);
      return control && control.invalid && control.touched;
    });
  }

  // Get missing required fields for each section
  getMissingCompanyFields(): string[] {
    if (!this.isCompanyUser || !this.formGroup) return [];
    const requiredFields = [
      { key: 'companyName', label: 'Company Name' }
    ];

    return requiredFields
      .filter(field => {
        const control = this.formGroup.get(field.key);
        return control && control.invalid && control.touched;
      })
      .map(field => field.label);
  }

  getMissingAddressFields(): string[] {
    if (!this.formGroup) return [];
    const requiredFields = [
      { key: 'postcodeTown', label: 'Street Name / Number' },
      { key: 'postalCode', label: 'Postal Code' },
      { key: 'city', label: 'City' },
      { key: 'otherCountry', label: 'Country (auto-selected)' }
    ];

    return requiredFields
      .filter(field => {
        const control = this.formGroup.get(field.key);
        return control && control.invalid && control.touched;
      })
      .map(field => field.label);
  }

  getMissingContactFields(): string[] {
    if (!this.formGroup) return [];
    const requiredFields = [
      { key: 'firstName', label: 'Name' },
      { key: 'lastName', label: 'Surname' },
      { key: 'phoneNumber', label: 'Phone No.' },
      { key: 'email', label: 'Email Address' }
    ];

    return requiredFields
      .filter(field => {
        const control = this.formGroup.get(field.key);
        return control && control.invalid && control.touched;
      })
      .map(field => field.label);
  }
}
